import { __awaiter } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { ButtonAlpha, isPossiblePhoneNumber } from '@yonderland/andes-react';
import { CustomerPhoneField } from '../CustomerPhoneField';
import { useDeliveryState } from '../../../../context/CheckoutDeliveryContext';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import { useAemConfig } from '@as-react/common-components';
import { FormProvider, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { ErrorMessage } from '@as-react/common-types';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeGetIsLoggedIn } from '../../../../redux/session/selectors/SessionSelector';
import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import CustomerUtil from '../../../../util/CustomerUtil';
import { customerMutations } from '@as-react/common-queries';
import { useMutation } from '@tanstack/react-query';
import { DeliverySheet } from '../DeliverySheet';
export const HomeDeliveryPhoneSelector = () => {
    var _a, _b;
    const { t } = useTranslation(I18N_NAMESPACES.checkout);
    const aemConfiguration = useAemConfig();
    const { defaultRequestParams, servicesEndpoint } = aemConfiguration;
    const isLoggedIn = useSelector(makeGetIsLoggedIn());
    const [{ info, openSelector, activeDeliveryOption }, dispatch] = useDeliveryState();
    const isOpen = openSelector === 'phone-number';
    const { updatePhoneNumber } = customerMutations({ servicesEndpoint, defaultRequestParams });
    const { mutate: updatePhoneNumberMutation } = useMutation(updatePhoneNumber());
    const phoneNumber = ((_a = info.deliveryAddress) === null || _a === void 0 ? void 0 : _a.phoneNumber) || ((_b = info.customerDetails) === null || _b === void 0 ? void 0 : _b.mobilePhoneNumber);
    const initialPhoneNumber = CustomerUtil.convertPhoneNumberPrefix(phoneNumber, '00', '+') || '';
    const validationSchema = Yup.object({
        phoneNumber: Yup.string().test('isPossiblePhoneNumber', t(ErrorMessage.INVALID_PHONE), val => !val || isPossiblePhoneNumber(val)),
        savePhoneNumber: Yup.boolean(),
    });
    const formProps = useForm({
        defaultValues: { phoneNumber: initialPhoneNumber, savePhoneNumber: false },
        mode: 'onTouched',
        resolver: yupResolver(validationSchema),
    });
    useEffect(() => {
        formProps.reset({
            phoneNumber: CustomerUtil.convertPhoneNumberPrefix(phoneNumber, '00', '+') || '',
        });
    }, [phoneNumber]);
    useEffect(() => {
        if (!initialPhoneNumber)
            return;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            const isValid = yield validationSchema.isValid({ mobilePhoneNumber: initialPhoneNumber });
            !isValid && formProps.trigger();
        }))();
    }, []);
    const handleSubmit = ({ phoneNumber, savePhoneNumber }) => {
        if (!phoneNumber || phoneNumber === initialPhoneNumber) {
            closeSelector();
            return;
        }
        const newPhoneNumber = CustomerUtil.convertPhoneNumberPrefix(phoneNumber, '+', '00');
        if (!savePhoneNumber) {
            updateDeliveryPhoneNumber(newPhoneNumber);
            closeSelector();
            return;
        }
        updatePhoneNumberMutation(newPhoneNumber, {
            onSuccess: () => {
                updateCustomerPhoneNumber(newPhoneNumber);
                updateDeliveryPhoneNumber(newPhoneNumber);
                closeSelector();
            },
        });
    };
    const closeSelector = () => {
        dispatch({ type: 'set_open_selector', payload: undefined });
    };
    const updateCustomerPhoneNumber = (phone) => {
        dispatch({ type: 'set_customer_phone_number', payload: phone });
    };
    const updateDeliveryPhoneNumber = (phone) => {
        dispatch({ type: 'set_delivery_phone_number', payload: phone });
    };
    const getContent = () => (_jsx(FormProvider, Object.assign({}, formProps, { children: _jsx("form", { onSubmit: formProps.handleSubmit(handleSubmit), noValidate: true, name: "phone-number-form", children: _jsx(CustomerPhoneField, { isRequired: activeDeliveryOption === null || activeDeliveryOption === void 0 ? void 0 : activeDeliveryOption.requiresPhoneNumber, showSavePhoneNumberCheckbox: isLoggedIn }) }) })));
    const getCTAContent = () => (_jsx(ButtonAlpha, { fullWidth: true, theme: "branded", text: t('checkout:checkout.delivery.flyout.update'), onClick: formProps.handleSubmit(handleSubmit) }));
    const cancelChangePhoneNumber = () => {
        dispatch({ type: 'set_open_selector', payload: undefined });
    };
    return DeliverySheet({
        isOpen,
        onClose: cancelChangePhoneNumber,
        getContent,
        showCTA: true,
        getCTAContent,
    });
};
